import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Camera,
  X,
  RotateCcw,
  Check,
  RotateCw,
  Crop,
  ZoomIn,
  ZoomOut,
  Undo,
} from "lucide-react";

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageData: string) => void;
  disableEditing?: boolean; // New prop to disable editing functionality
}

const CameraModal: React.FC<CameraModalProps> = ({
  isOpen,
  onClose,
  onCapture,
  disableEditing = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Camera switching state
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment",
  ); // Default to rear camera
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [currentCameraId, setCurrentCameraId] = useState<string>("");

  // Photo editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedImage, setEditedImage] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [editHistory, setEditHistory] = useState<string[]>([]);

  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      setAvailableCameras(cameras);

      // If we have cameras and no current camera is selected, select the first one
      if (cameras.length > 0 && !currentCameraId) {
        // Try to find rear camera first
        const rearCamera = cameras.find(
          (camera) =>
            camera.label.toLowerCase().includes("back") ||
            camera.label.toLowerCase().includes("rear") ||
            camera.label.toLowerCase().includes("environment"),
        );
        if (rearCamera) {
          setCurrentCameraId(rearCamera.deviceId);
          setFacingMode("environment");
        } else {
          // Fallback to first available camera
          setCurrentCameraId(cameras[0].deviceId);
          setFacingMode("user");
        }
      }
    } catch (err) {
      console.warn("Error enumerating cameras:", err);
    }
  }, [currentCameraId]);

  const startCamera = useCallback(async () => {
    try {
      setError(null);

      // Enumerate cameras if not already done
      if (availableCameras.length === 0) {
        await enumerateCameras();
      }

      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          ...(currentCameraId
            ? { deviceId: { exact: currentCameraId } }
            : { facingMode }),
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError(
        "Unable to access camera. Please check permissions and try again.",
      );
    }
  }, [facingMode, currentCameraId, availableCameras.length, enumerateCameras]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  const switchCamera = useCallback(async () => {
    if (availableCameras.length < 2) return;

    // Stop current stream
    stopCamera();

    // Find the next camera
    const currentIndex = availableCameras.findIndex(
      (camera) => camera.deviceId === currentCameraId,
    );
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    const nextCamera = availableCameras[nextIndex];

    setCurrentCameraId(nextCamera.deviceId);

    // Update facing mode based on camera label
    const isRearCamera =
      nextCamera.label.toLowerCase().includes("back") ||
      nextCamera.label.toLowerCase().includes("rear") ||
      nextCamera.label.toLowerCase().includes("environment");
    setFacingMode(isRearCamera ? "environment" : "user");

    // Start camera with new device
    setTimeout(() => startCamera(), 100);
  }, [availableCameras, currentCameraId, stopCamera, startCamera]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const imageData = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedImage(imageData);
    stopCamera();
  }, [stopCamera]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setIsEditing(false);
    setEditedImage(null);
    setRotation(0);
    setZoom(1);
    setCropArea(null);
    setIsCropping(false);
    setCropStart(null);
    setEditHistory([]);
    startCamera();
  }, [startCamera]);

  const confirmPhoto = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage);
      onClose();
    }
  }, [capturedImage, onCapture, onClose]);

  // Photo editing functions
  const startEditing = useCallback(() => {
    if (capturedImage) {
      setIsEditing(true);
      setEditedImage(capturedImage);
      setEditHistory([capturedImage]);
    }
  }, [capturedImage]);

  const applyEdit = useCallback((newImageData: string) => {
    setEditedImage(newImageData);
    setEditHistory((prev) => [...prev, newImageData]);
  }, []);

  const undoEdit = useCallback(() => {
    if (editHistory.length > 1) {
      const newHistory = [...editHistory];
      newHistory.pop();
      const previousImage = newHistory[newHistory.length - 1];
      setEditedImage(previousImage);
      setEditHistory(newHistory);
    }
  }, [editHistory]);

  const rotateImage = useCallback(
    (degrees: number) => {
      if (!editedImage || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        // Calculate new canvas size for rotation
        const angle = (degrees * Math.PI) / 180;
        const cos = Math.abs(Math.cos(angle));
        const sin = Math.abs(Math.sin(angle));
        const newWidth = img.width * cos + img.height * sin;
        const newHeight = img.width * sin + img.height * cos;

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Clear and translate to center
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(angle);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        const rotatedData = canvas.toDataURL("image/jpeg", 0.8);
        applyEdit(rotatedData);
      };
      img.src = editedImage;
    },
    [editedImage, applyEdit],
  );

  const zoomImage = useCallback((scale: number) => {
    setZoom((prev) => Math.max(0.5, Math.min(3, prev * scale)));
  }, []);

  const cropImage = useCallback(() => {
    if (!editedImage || !cropArea || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height,
      );

      const croppedData = canvas.toDataURL("image/jpeg", 0.8);
      applyEdit(croppedData);
      setCropArea(null);
    };
    img.src = editedImage;
  }, [editedImage, cropArea, applyEdit]);

  // Crop selection functions
  const startCrop = useCallback(() => {
    setIsCropping(true);
    setCropArea(null);
  }, []);

  const handleImageMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isCropping || !isEditing) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setCropStart({ x, y });
      setCropArea({ x, y, width: 0, height: 0 });
    },
    [isCropping, isEditing],
  );

  const handleImageMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isCropping || !cropStart || !isEditing) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const width = Math.abs(x - cropStart.x);
      const height = Math.abs(y - cropStart.y);
      const startX = Math.min(x, cropStart.x);
      const startY = Math.min(y, cropStart.y);

      setCropArea({ x: startX, y: startY, width, height });
    },
    [isCropping, cropStart, isEditing],
  );

  const handleImageMouseUp = useCallback(() => {
    if (isCropping) {
      setIsCropping(false);
      setCropStart(null);
    }
  }, [isCropping]);

  const cancelCrop = useCallback(() => {
    setIsCropping(false);
    setCropArea(null);
    setCropStart(null);
  }, []);

  const finishEditing = useCallback(() => {
    if (editedImage) {
      setCapturedImage(editedImage);
    }
    setIsEditing(false);
    setEditedImage(null);
    setRotation(0);
    setZoom(1);
    setCropArea(null);
    setIsCropping(false);
    setCropStart(null);
    setEditHistory([]);
  }, [editedImage]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedImage(null);
    setRotation(0);
    setZoom(1);
    setCropArea(null);
    setEditHistory([]);
  }, []);

  useEffect(() => {
    if (isOpen) {
      enumerateCameras().then(() => {
        startCamera();
      });
    } else {
      stopCamera();
      setCapturedImage(null);
      setError(null);
      // Reset editing state
      setIsEditing(false);
      setEditedImage(null);
      setRotation(0);
      setZoom(1);
      setCropArea(null);
      setIsCropping(false);
      setCropStart(null);
      setEditHistory([]);
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, enumerateCameras, startCamera, stopCamera]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Take Student Photo
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Camera/Preview Area */}
        <div className="relative bg-black">
          {error ? (
            <div className="flex items-center justify-center h-96 text-white">
              <div className="text-center">
                <Camera className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium mb-2">Camera Error</p>
                <p className="text-sm text-gray-400 max-w-md">{error}</p>
              </div>
            </div>
          ) : capturedImage ? (
            <div className="relative">
              <div
                className={`flex items-center justify-center h-96 bg-gray-100 ${
                  isCropping ? "cursor-crosshair" : ""
                }`}
                onMouseDown={handleImageMouseDown}
                onMouseMove={handleImageMouseMove}
                onMouseUp={handleImageMouseUp}
                onMouseLeave={handleImageMouseUp}
              >
                <img
                  src={isEditing ? editedImage || capturedImage : capturedImage}
                  alt="Captured"
                  className="max-h-full max-w-full object-contain"
                  style={{
                    transform: isEditing
                      ? `scale(${zoom}) rotate(${rotation}deg)`
                      : undefined,
                    transition: "transform 0.2s ease",
                    pointerEvents: isCropping ? "none" : "auto",
                  }}
                />
                {isEditing && cropArea && (
                  <div
                    className="absolute border-2 border-blue-500 bg-blue-500/20"
                    style={{
                      left: `${cropArea.x}px`,
                      top: `${cropArea.y}px`,
                      width: `${cropArea.width}px`,
                      height: `${cropArea.height}px`,
                    }}
                  >
                    {/* Crop area resize handles */}
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 rounded-full border border-white"></div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border border-white"></div>
                    <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 rounded-full border border-white"></div>
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border border-white"></div>
                  </div>
                )}
                {isCropping && (
                  <div className="absolute inset-0 bg-blue-500/10 pointer-events-none">
                    <div className="absolute top-2 left-2 bg-black/75 text-white px-2 py-1 rounded text-sm">
                      Click and drag to select crop area
                    </div>
                  </div>
                )}
              </div>

              {/* Editing Controls */}
              {isEditing && (
                <div className="absolute top-4 left-4 right-4 bg-black/75 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => rotateImage(-90)}
                        className="p-2 bg-white/20 rounded-md hover:bg-white/30 transition-colors"
                        title="Rotate Left"
                      >
                        <RotateCcw className="h-4 w-4 text-white" />
                      </button>
                      <button
                        onClick={() => rotateImage(90)}
                        className="p-2 bg-white/20 rounded-md hover:bg-white/30 transition-colors"
                        title="Rotate Right"
                      >
                        <RotateCw className="h-4 w-4 text-white" />
                      </button>
                      <button
                        onClick={() => zoomImage(1.2)}
                        className="p-2 bg-white/20 rounded-md hover:bg-white/30 transition-colors"
                        title="Zoom In"
                      >
                        <ZoomIn className="h-4 w-4 text-white" />
                      </button>
                      <button
                        onClick={() => zoomImage(0.8)}
                        className="p-2 bg-white/20 rounded-md hover:bg-white/30 transition-colors"
                        title="Zoom Out"
                      >
                        <ZoomOut className="h-4 w-4 text-white" />
                      </button>
                      <button
                        onClick={() => {
                          if (cropArea && !isCropping) {
                            cropImage();
                          } else if (isCropping) {
                            cancelCrop();
                          } else {
                            startCrop();
                          }
                        }}
                        className={`p-2 rounded-md transition-colors ${
                          isCropping
                            ? "bg-red-500/80 hover:bg-red-500/90"
                            : cropArea
                              ? "bg-green-500/80 hover:bg-green-500/90"
                              : "bg-white/20 hover:bg-white/30"
                        }`}
                        title={
                          isCropping
                            ? "Cancel Crop"
                            : cropArea
                              ? "Apply Crop"
                              : "Crop Image"
                        }
                      >
                        <Crop className="h-4 w-4 text-white" />
                      </button>
                      <button
                        onClick={undoEdit}
                        disabled={editHistory.length <= 1}
                        className="p-2 bg-white/20 rounded-md hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Undo"
                      >
                        <Undo className="h-4 w-4 text-white" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={cancelEditing}
                        className="px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={finishEditing}
                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-96 object-cover"
              />
              {!isStreaming && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="text-white text-center">
                    <Camera className="h-12 w-12 mx-auto mb-2 animate-pulse" />
                    <p>Starting camera...</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          {error ? (
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setError(null);
                  startCamera();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : capturedImage ? (
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <button
                  onClick={retakePhoto}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  Retake Photo
                </button>
                {!isEditing && !disableEditing && (
                  <button
                    onClick={startEditing}
                    className="flex items-center gap-2 px-4 py-2 text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    <Crop className="h-4 w-4" />
                    Edit Photo
                  </button>
                )}
              </div>
              <button
                onClick={confirmPhoto}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Check className="h-4 w-4" />
                Use This Photo
              </button>
            </div>
          ) : (
            <div className="flex justify-center items-center gap-4">
              {availableCameras.length > 1 && (
                <button
                  onClick={switchCamera}
                  disabled={!isStreaming}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  title="Switch Camera"
                >
                  <RotateCcw className="h-4 w-4" />
                  Switch
                </button>
              )}
              <button
                onClick={capturePhoto}
                disabled={!isStreaming}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <Camera className="h-5 w-5" />
                Capture Photo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraModal;
